import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard, LoadingBlock, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { euro, formatDate, BOOKING_STATUS_LABELS, VERIFICATION_LABELS } from "@/lib/format";
import { Inbox, BadgeEuro, ShieldCheck, Clock, AlertCircle, HandHeart } from "lucide-react";

export default function ProDashboard() {
  const { user } = useAuth();
  const { data: prof } = useQuery({ queryKey: ["prof-profile"], queryFn: async () => (await api.get("/professional/profile")).data });
  const { data: bkData, isLoading } = useQuery({ queryKey: ["prof-bookings"], queryFn: async () => (await api.get("/professional/bookings")).data });
  const { data: reqData } = useQuery({ queryKey: ["prof-requests"], queryFn: async () => (await api.get("/professional/requests")).data });
  const bookings = bkData?.bookings || [];
  const profile = prof?.profile;
  const mineReqs = reqData?.mine || [];
  const takenCount = mineReqs.length;
  const agreedTotal = mineReqs.reduce((s, r) => s + (r.agreed_price_cents || 0), 0);

  const pending = bookings.filter((b) => b.booking_status === "in_attesa_di_conferma");

  return (
    <DashboardLayout title="Dashboard">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#102A2A]">Ciao, {user?.first_name}!</h2>
        <p className="text-[#5C6B68] mt-1">Ecco il riepilogo della tua attività su EtruriaCare.</p>
      </div>

      {profile && profile.verification_status !== "verificato" && (
        <div data-testid="verify-warning" className="mb-6 flex items-start gap-3 bg-[#FBF0D6] border border-[#D99A00]/40 text-[#8a6400] rounded-xl px-4 py-4">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Profilo in verifica</p>
            <p className="text-sm">Il tuo profilo è <strong>{VERIFICATION_LABELS[profile.verification_status]}</strong>. Non sei ancora visibile ai pazienti. Completa il profilo e attendi l'approvazione dell'amministratore.</p>
            <Link to="/pro/profilo" className="text-sm font-semibold underline mt-1 inline-block">Completa il profilo →</Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={HandHeart} label="Richieste prese in carico" value={takenCount} tone="dark" testid="stat-taken" />
        <StatCard icon={BadgeEuro} label="Tariffe concordate (totale)" value={euro(agreedTotal)} tone="petrol" testid="stat-agreed-total" />
        <StatCard icon={Inbox} label="Richieste in attesa" value={pending.length} tone="yellow" testid="stat-pending" />
        <StatCard icon={ShieldCheck} label="Stato profilo" value={VERIFICATION_LABELS[profile?.verification_status] || "—"} tone={profile?.verification_status === "verificato" ? "petrol" : "yellow"} testid="stat-status" />
      </div>

      <div className="flex items-center gap-2 mb-4 text-sm">
        <Clock className="h-4 w-4 text-[#087A73]" />
        <span className="text-[#5C6B68]">Ricezione nuove richieste:</span>
        <StatusBadge status={profile?.accepting_requests ? "verificato" : "da_verificare"} label={profile?.accepting_requests ? "Attiva" : "In pausa"} />
      </div>

      <h3 className="font-bold text-[#102A2A] mb-4">Richieste in attesa di risposta</h3>
      {isLoading ? <LoadingBlock /> : pending.length === 0 ? (
        <EmptyState title="Nessuna richiesta in attesa" subtitle="Le nuove richieste dei pazienti appariranno qui." />
      ) : (
        <div className="space-y-3">
          {pending.map((b) => (
            <Card key={b.id} className="flex items-center justify-between">
              <div><p className="font-semibold text-[#102A2A]">{b.service_name}</p><p className="text-sm text-[#5C6B68]">{formatDate(b.scheduled_date)} · {b.start_time} · {b.municipality}</p></div>
              <div className="text-right"><p className="font-bold text-[#087A73]">{euro(b.price_cents)}</p><Link to="/pro/richieste" className="text-sm text-[#087A73] font-semibold">Gestisci →</Link></div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
